// src/pages/CheckoutPage.jsx
// Steps: 1=Shipping Info, 2=Shipping Method (UPS), 3=Artwork, 4=Payment
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ref, uploadBytesResumable, getDownloadURL, storage } from "../services/mysqlStorage";
import { doc, getDoc } from "../services/mysqlStore";
import { db } from "../services/mysqlStore";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { fetchShippingEstimate } from "../services/sinaliteService";
import { getSavedCartKey } from "../services/savedCart";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadCompanyInfo, getCompanyInfo } from "../constants/companyInfo";
import API_BASE from "../services/apiClient";
import "./CheckoutPage.css";

const STRIPE_PUBLISHABLE_KEY = "pk_live_51TgUXnAAAvOEiM43mUI6GSZBeUzr9BRtBuj0R8FHSHT9Ml2nwSFR38gWJWAykuHtaA8rp0nkWXPFpcckofH2oTdQ00fBkNlkei";
const CLOUD_FUNCTION_URL = `${API_BASE}/payment-intent`;
const HST_RATE = 0.13; // Ontario HST
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const CA_PROVINCES = [
  { code: "AB", name: "Alberta" },
  { code: "BC", name: "British Columbia" },
  { code: "MB", name: "Manitoba" },
  { code: "NB", name: "New Brunswick" },
  { code: "NL", name: "Newfoundland and Labrador" },
  { code: "NS", name: "Nova Scotia" },
  { code: "NT", name: "Northwest Territories" },
  { code: "NU", name: "Nunavut" },
  { code: "ON", name: "Ontario" },
  { code: "PE", name: "Prince Edward Island" },
  { code: "QC", name: "Quebec" },
  { code: "SK", name: "Saskatchewan" },
  { code: "YT", name: "Yukon" },
];

const US_STATES = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" }, { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" }, { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" }, { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" }, { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" }, { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" }, { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" }, { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" }, { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" }, { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" }, { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" }, { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" }, { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" }, { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" }, { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
];

const ALLOWED_TYPES = [".pdf"]; // PDF only — print-ready files
const MAX_FILE_SIZE_MB = 50;

/* ── Success Screen CSS injected inline so no extra file needed ── */
const SUCCESS_CSS = `
.checkout-success-wrap {
  position: relative;
  overflow: hidden;
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}
.cs-confetti-wrap {
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 0;
  pointer-events: none;
}
.cs-dot {
  position: absolute;
  top: -10px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  animation: cs-confetti 1s ease forwards;
}
.checkout-success {
  text-align: center;
  max-width: 480px;
  width: 100%;
  background: #fff;
  border-radius: 20px;
  padding: 40px 32px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.1);
  position: relative;
  z-index: 1;
}
.cs-icon-wrap {
  position: relative;
  width: 90px;
  height: 90px;
  margin: 0 auto 24px;
}
.cs-circle {
  width: 90px;
  height: 90px;
  animation: cs-pop 0.5s cubic-bezier(0.36,0.07,0.19,0.97) both;
}
.cs-circle svg { width: 100%; height: 100%; }
.cs-logo-circle {
  position: relative;
  border-radius: 50%;
  overflow: visible;
  background: #fff;
  border: 3px solid #fff;
  box-shadow: 0 6px 20px rgba(0,0,0,0.12);
}
.cs-logo-img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  display: block;
}
.cs-check-badge {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #fff;
  padding: 2px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.18);
}
.cs-check-badge svg { width: 100%; height: 100%; display: block; }
.cs-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid rgba(74,222,128,0.4);
  animation: cs-ring 1s ease 0.4s forwards;
}
.cs-title {
  font-size: 26px;
  font-weight: 900;
  color: #1a1a2e;
  margin: 0 0 8px;
}
.cs-subtitle {
  font-size: 15px;
  color: #555;
  margin: 0 0 6px;
}
.cs-email {
  font-size: 13px;
  color: #888;
  margin: 0 0 24px;
}
.cs-summary {
  background: #f8fafc;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: left;
}
.cs-summary-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13.5px;
  color: #374151;
}
.cs-summary-icon { flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center; color: #00aadd; }
.checkout-success-btns {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
}
.cs-btn-primary { padding: 13px 28px; font-size: 15px; }
.cs-btn-outline  { padding: 13px 28px; font-size: 15px; }
`;

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: { fontSize: "15px", color: "#1a1a2e", fontFamily: "'Barlow', sans-serif", "::placeholder": { color: "#9ca3af" } },
    invalid: { color: "#ef4444" },
  },
};

// ── Icons ──────────────────────────────────────────────────────────────────
const LockIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>;
const ShieldIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
const TruckIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><circle cx="7" cy="21" r="2"/><path d="M9 21h6"/><circle cx="17" cy="21" r="2"/><path d="M17 3v5h2a2 2 0 0 1 2 2v7"/></svg>;
const CheckIcon = () => <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="m9 11 3 3L22 4"/></svg>;
const PrinterIcon = () => <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>;
const UploadIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>;
const FileIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>;
const TrashIcon = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>;
const PinIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>;

function formatBytes(bytes) {
  if (!bytes) return "0 KB";
  var kb = bytes / 1024;
  if (kb < 1024) return kb.toFixed(0) + " KB";
  return (kb / 1024).toFixed(1) + " MB";
}

// ── Inner Checkout Form ────────────────────────────────────────────────────
function CheckoutForm({ succeeded, setSucceeded }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { items, totalPrice, clearCart } = useCart();
  const { user, profile } = useAuth();

  // Steps: 1=shipping info, 2=shipping method, 3=artwork, 4=payment
  const [step, setStep] = useState(1);
  const [processing, setProcessing] = useState(false);
  // Ref-based lock (checked synchronously, unlike React state which updates
  // async/batched) — this is what actually stops a fast double-click/tap
  // from firing handlePayment twice before the button's `disabled` prop
  // re-renders.
  const submittingRef = useRef(false);
  // One idempotency key per checkout attempt — sent to the backend so that
  // even if the request itself gets duplicated (retry, double POST, etc.)
  // the server only ever creates one PaymentIntent / one Sinalite order for it.
  const idempotencyKeyRef = useRef(null);
  const [cardError, setCardError] = useState("");
  const [orderId, setOrderId] = useState("");

  const [shipping, setShipping] = useState({
    fullName: profile?.fullName || user?.displayName || "",
    email: user?.email || "",
    phone: profile?.phone || "",
    address: profile?.address || "",
    address2: "",
    city: profile?.city || "",
    state: profile?.state || "",
    postcode: profile?.postcode || "",
    country: profile?.country || "CA",
  });

  // Profile may load after the page mounts; fill only fields the customer hasn't typed.
  useEffect(() => {
    if (!profile && !user) return;
    setShipping(prev => ({
      ...prev,
      fullName: prev.fullName || profile?.fullName || user?.displayName || "",
      email: prev.email || user?.email || "",
      phone: prev.phone || profile?.phone || "",
      address: prev.address || profile?.address || "",
      city: prev.city || profile?.city || "",
      state: prev.state || profile?.state || "",
      postcode: prev.postcode || profile?.postcode || "",
      country: prev.address ? prev.country : (profile?.country || prev.country),
    }));
  }, [profile, user]);

  // Shipping method (UPS)
  const [shippingOptions, setShippingOptions] = useState([]);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [shippingError, setShippingError] = useState("");

  // Artwork (pre-filled with any files already uploaded from the cart page)
  const [artwork, setArtwork] = useState(function () {
    var initial = {};
    items.forEach(function (item) {
      if (item.artworkUrl) initial[item.cartId] = { file: null, progress: 100, url: item.artworkUrl, name: item.artworkName || "Artwork.pdf", size: 0, error: null };
    });
    return initial;
  });
  const [artworkError, setArtworkError] = useState("");

  // Coupon
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null); // { code, discountPercent }
  const [couponStatus, setCouponStatus] = useState(null);   // null | "checking" | "applied" | "invalid"

  async function applyCoupon() {
    var code = couponInput.trim().toUpperCase();
    if (!code) return;
    setCouponStatus("checking");
    try {
      var snap = await getDoc(doc(db, "coupons", code));
      if (snap.exists() && snap.data().active) {
        setAppliedCoupon({ code, discountPercent: snap.data().discountPercent });
        setCouponStatus("applied");
      } else {
        setAppliedCoupon(null);
        setCouponStatus("invalid");
      }
    } catch (err) {
      setAppliedCoupon(null);
      setCouponStatus("invalid");
    }
  }

  function removeCoupon() {
    setAppliedCoupon(null);
    setCouponInput("");
    setCouponStatus(null);
  }

  var discountAmount = appliedCoupon ? totalPrice * (appliedCoupon.discountPercent / 100) : 0;
  var discountedSubtotal = totalPrice - discountAmount;

  // Saved cart: stores the cart + uploaded artwork on the server so an admin can follow up
  // with the customer even if they never pay or pick a delivery method.
  const savedCartKeyRef = useRef(null);
  if (!savedCartKeyRef.current) savedCartKeyRef.current = getSavedCartKey();
  const [followUpStatus, setFollowUpStatus] = useState(""); // "" | "saving" | "saved" | "error"

  async function saveCartForFollowUp() {
    if (!items.length || !/^\S+@\S+\.\S+$/.test(shipping.email.trim())) return false;
    try {
      var res = await fetch(`${API_BASE}/saved-carts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cartKey: savedCartKeyRef.current,
          customer: shipping,
          couponCode: appliedCoupon ? appliedCoupon.code : null,
          items: items.map(function (item) {
            var art = artwork[item.cartId];
            return {
              productId: item.productId,
              productName: item.productName,
              optionMap: item.optionMap || {},
              optionLabels: item.optionLabels || {},
              displayPrice: item.displayPrice || 0,
              artworkUrl: art && art.url ? art.url : null,
              artworkName: art && art.url ? art.name : null,
            };
          }),
        }),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  async function handleSendToTeam() {
    setFollowUpStatus("saving");
    setFollowUpStatus((await saveCartForFollowUp()) ? "saved" : "error");
  }

  // Keep the saved cart in sync as artwork finishes uploading (only after step 1 gave us contact details)
  var uploadedSignature = items.map(function (item) { return artwork[item.cartId] && artwork[item.cartId].url ? artwork[item.cartId].url : ""; }).join("|");
  useEffect(function () {
    if (step < 2 || !uploadedSignature.replace(/\|/g, "")) return;
    saveCartForFollowUp();
  }, [uploadedSignature]);

  function handleShipping(e) {
    setShipping(function (s) { return { ...s, [e.target.name]: e.target.value }; });
  }

  function validateShipping() {
    if (!shipping.fullName.trim()) return "Full name required";
    if (!shipping.email.trim()) return "Email required";
    if (!shipping.address.trim()) return "Address required";
    if (!shipping.city.trim()) return "City required";
    if (!shipping.state.trim()) return "State/Province required";
    if (!shipping.postcode.trim()) return "Postcode / ZIP required";
    return null;
  }

  // Step 1 → Step 2: fetch shipping options from Sinalite
  async function handleShippingInfoNext(e) {
    e.preventDefault();
    var err = validateShipping();
    if (err) { setCardError(err); return; }
    setCardError("");
    setShippingLoading(true);
    setShippingError("");
    saveCartForFollowUp();
    try {
      const cartItems = items.map(function (item) {
        return { productId: item.productId, optionMap: item.optionMap || {} };
      });
      const options = await fetchShippingEstimate(cartItems, {
        state: shipping.state,
        country: shipping.country,
        zip: shipping.postcode,
      });
      setShippingOptions(options);
      setSelectedShipping(options.length > 0 ? options[0] : null);
      setStep(2);
    } catch {
      // Don't block the customer: they can still upload artwork and our team will follow up on delivery.
      setShippingOptions([]);
      setSelectedShipping(null);
      setShippingError("We couldn't calculate delivery options automatically.");
      setStep(2);
    } finally {
      setShippingLoading(false);
    }
  }

  // Step 3: Artwork upload
  function handleFileSelect(cartId, fileList) {
    var file = fileList[0];
    if (!file) return;
    var ext = "." + file.name.split(".").pop().toLowerCase();
    if (ALLOWED_TYPES.indexOf(ext) === -1) { setArtworkError("File type not supported."); return; }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) { setArtworkError("File too large (max 50MB)."); return; }
    setArtworkError("");
    setArtwork(function (prev) {
      return { ...prev, [cartId]: { file, progress: 0, url: null, name: file.name, size: file.size, error: null } };
    });
    var storagePath = "artwork/" + (user?.uid || "guest") + "/" + Date.now() + "_" + file.name;
    var storageRef = ref(storage, storagePath);
    var uploadTask = uploadBytesResumable(storageRef, file);
    uploadTask.on(
      "state_changed",
      function (snapshot) {
        var pct = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        setArtwork(function (prev) { return { ...prev, [cartId]: { ...prev[cartId], progress: pct } }; });
      },
      function (err) {
        setArtwork(function (prev) { return { ...prev, [cartId]: { ...prev[cartId], error: "Upload failed" + (err && err.message ? ": " + err.message : ".") } }; });
      },
      function () {
        getDownloadURL(uploadTask.snapshot.ref).then(function (url) {
          setArtwork(function (prev) { return { ...prev, [cartId]: { ...prev[cartId], url, progress: 100 } }; });
        });
      }
    );
  }

  function removeArtwork(cartId) {
    setArtwork(function (prev) { var c = { ...prev }; delete c[cartId]; return c; });
  }

  // Step 4: Payment + Sinalite order
  async function handlePayment(e) {
    e.preventDefault();
    // Hard stop: if a submit is already in flight, ignore this one.
    // Checked first and synchronously so a rapid double-click can't sneak
    // a second call in before React re-renders the disabled button.
    if (submittingRef.current) return;
    if (!stripe || !elements) {
      setCardError("Payment form is still loading — please wait a moment and try again.");
      return;
    }
    submittingRef.current = true;
    setProcessing(true);
    setCardError("");
    // Reuse the same idempotency key across retries of this order (e.g. if
    // Stripe requires 3D-Secure and confirmCardPayment is retried) so the
    // backend can safely dedupe.
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current =
        (window.crypto && window.crypto.randomUUID)
          ? window.crypto.randomUUID()
          : "ord_" + Date.now() + "_" + Math.random().toString(36).slice(2);
    }

    try {
      var shippingCost = selectedShipping ? Math.round(selectedShipping.price * 100) : 0;
      var subtotalCents = Math.round(discountedSubtotal * 100);
      var hstCents = Math.round((discountedSubtotal + (selectedShipping?.price || 0)) * HST_RATE * 100);
      var totalCents = subtotalCents + shippingCost + hstCents;

      // Build shipping/billing info for Sinalite
      var nameParts = shipping.fullName.trim().split(" ");
      var firstName = nameParts[0] || "";
      var lastName = nameParts.slice(1).join(" ") || "-";

      var shippingInfo = {
        ShipFName: firstName,
        ShipLName: lastName,
        ShipEmail: shipping.email,
        ShipAddr: shipping.address,
        ShipAddr2: shipping.address2 || "",
        ShipCity: shipping.city,
        ShipState: shipping.state,
        ShipZip: shipping.postcode,
        ShipCountry: shipping.country,
        ShipPhone: shipping.phone || "",
        ShipMethod: selectedShipping ? selectedShipping.service : "",
      };

      // Load company info from admin panel (Firestore)
      const COMPANY_INFO = await loadCompanyInfo();

      // Load user profile to check for notificationEmail override (sa Account page set ho)
      var notificationEmail = COMPANY_INFO.email; // Default
      if (user?.uid) {
        try {
          const userProfileSnap = await getDoc(doc(db, "users", user.uid));
          const userProfile = userProfileSnap?.data();
          if (userProfile?.notificationEmail) {
            notificationEmail = userProfile.notificationEmail;
            console.log("[Checkout] Using user's notification email:", notificationEmail);
          }
        } catch (err) {
          console.warn("[Checkout] Could not load user profile for notificationEmail, using admin panel email:", err);
        }
      }

      // BILLING INFO: Always use company address (Circuit Printing), not customer address
      // This ensures invoices go to the company, not the customer
      var billingInfo = {
        BillFName: COMPANY_INFO.name.split(" ")[0],
        BillLName: COMPANY_INFO.name.split(" ")[1] || "",
        BillEmail: notificationEmail, // Notification email — admin override karte ho Account page se
        BillAddr: COMPANY_INFO.address,
        BillAddr2: "",
        BillCity: COMPANY_INFO.city,
        BillState: COMPANY_INFO.state,
        BillZip: COMPANY_INFO.postcode,
        BillCountry: COMPANY_INFO.country,
        BillPhone: COMPANY_INFO.phone,
      };

      // Build cart items with artwork URLs for Sinalite
      var cartItemsForOrder = items.map(function (item) {
        var art = artwork[item.cartId];
        return {
          productId: item.productId,
          productName: item.productName,
          sinalitePrice: item.sinalitePrice || 0,
          quantity: 1,
          optionMap: item.optionMap || {},
          artworkUrl: art && art.url ? art.url : null,
        };
      });

      // Call Cloud Function: creates Stripe PaymentIntent + places Sinalite order
      console.log("[Checkout] Calling Cloud Function...", CLOUD_FUNCTION_URL);
      var response = await fetch(CLOUD_FUNCTION_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: totalCents,
          currency: "cad",
          cartItems: cartItemsForOrder,
          shippingInfo,
          billingInfo,
          notes: "",
          userId: user?.uid || null,
          couponCode: appliedCoupon ? appliedCoupon.code : null,
          discountPercent: appliedCoupon ? appliedCoupon.discountPercent : 0,
          subtotalCents,
          shippingCents: shippingCost,
          hstRate: HST_RATE * 100,
          hstCents,
          idempotencyKey: idempotencyKeyRef.current,
          savedCartKey: savedCartKeyRef.current,
        }),
      });
      console.log("[Checkout] Cloud Function responded, status:", response.status);

      var rawText = await response.text();
      var data;
      try {
        data = JSON.parse(rawText);
      } catch (parseErr) {
        console.error("[Checkout] Response was not valid JSON:", rawText);
        throw new Error("Server returned an unexpected response (not JSON). Check the Cloud Function logs.");
      }
      console.log("[Checkout] Parsed response data:", data);

      if (!response.ok || !data.clientSecret) throw new Error(data.error || "Payment setup failed — no clientSecret returned.");

      // Confirm card payment via Stripe
      console.log("[Checkout] Confirming card payment with Stripe...");
      var result = await stripe.confirmCardPayment(data.clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: shipping.fullName,
            email: shipping.email,
            phone: shipping.phone,
            address: { line1: shipping.address, city: shipping.city, postal_code: shipping.postcode, country: shipping.country },
          },
        },
      });
      console.log("[Checkout] Stripe confirmCardPayment result:", result);

      if (result.error) { console.error("[Checkout] Stripe error:", result.error); setCardError(result.error.message); submittingRef.current = false; setProcessing(false); return; }

      if (result.paymentIntent.status === "succeeded") {
        await fetch(`${API_BASE}/payment-complete`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ orderId: data.orderId, paymentIntentId: result.paymentIntent.id }),
        }).catch(error => console.warn("[Checkout] Could not finalize payment record:", error));
        console.log("[Checkout] Payment succeeded! Showing success screen.");
        setOrderId(data.orderId || "");
        clearCart();
        setSucceeded(true);
      } else {
        // Anything other than "succeeded" (e.g. requires_action / 3D-Secure
        // still pending, processing, etc.) — show a visible message instead
        // of silently doing nothing and leaving the button stuck.
        console.warn("[Checkout] PaymentIntent status was not 'succeeded':", result.paymentIntent.status);
        setCardError("Payment was not completed (status: " + result.paymentIntent.status + "). Please try again.");
        submittingRef.current = false;
        setProcessing(false);
      }
    } catch (err) {
      console.error("[Checkout] handlePayment error:", err);
      setCardError(err.message || "Payment failed. Please try again.");
      submittingRef.current = false;
      setProcessing(false);
    }
  }

  // ── Success Screen ──────────────────────────────────────────────────────
  if (succeeded) {
    return (
      <><style>{SUCCESS_CSS}</style><div className="checkout-success-wrap">
        <style>{`
          @keyframes cs-pop { 0% { transform: scale(0.5); opacity: 0; } 70% { transform: scale(1.1); } 100% { transform: scale(1); opacity: 1; } }
          @keyframes cs-check { 0% { stroke-dashoffset: 100; opacity: 0; } 100% { stroke-dashoffset: 0; opacity: 1; } }
          @keyframes cs-fade-up { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes cs-confetti { 0% { transform: translateY(0) rotate(0deg); opacity: 1; } 100% { transform: translateY(120px) rotate(720deg); opacity: 0; } }
          @keyframes cs-ring { 0% { transform: scale(0.8); opacity: 0.6; } 100% { transform: scale(1.6); opacity: 0; } }
        `}</style>

        {/* Confetti dots */}
        <div className="cs-confetti-wrap" aria-hidden="true">
          {[...Array(12)].map((_, i) => (
            <div key={i} className="cs-dot" style={{
              left: (8 + i * 7.5) + "%",
              background: ["#00aadd","#e91e8c","#f5c200","#4ade80","#a78bfa"][i % 5],
              animationDelay: (i * 0.08) + "s",
              animationDuration: (0.8 + Math.random() * 0.4) + "s",
            }} />
          ))}
        </div>

        <div className="checkout-success anim-pop-in">
          {/* Logo circle with success checkmark badge */}
          <div className="cs-icon-wrap">
            <div className="cs-ring" />
            <div className="cs-circle cs-logo-circle">
              <img src="/logo.jpeg" alt="Circuit Printing Solutions" className="cs-logo-img" />
              <span className="cs-check-badge">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="11" fill="#4ade80" />
                  <path
                    d="M7 12.5l3 3 7-7"
                    stroke="#fff"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeDasharray="20"
                    strokeDashoffset="20"
                    style={{ animation: "cs-check 0.6s ease 0.4s forwards" }}
                  />
                </svg>
              </span>
            </div>
          </div>

          {/* Text */}
          <div className="cs-text" style={{ animation: "cs-fade-up 0.5s ease 0.5s both" }}>
            <h2 className="cs-title">Thank You, {shipping.fullName.split(" ")[0]}!</h2>
            <p className="cs-subtitle">Your order has been placed and sent to print.</p>
            <p className="cs-email">
              A confirmation will be sent to <strong>{shipping.email}</strong>
            </p>
          </div>

          {/* Order summary strip */}
          <div className="cs-summary" style={{ animation: "cs-fade-up 0.5s ease 0.7s both" }}>
            <div className="cs-summary-item">
              <span className="cs-summary-icon"><PrinterIcon /></span>
              <span>Your files are in the print queue</span>
            </div>
            <div className="cs-summary-item">
              <span className="cs-summary-icon"><TruckIcon /></span>
              <span>Shipping via {shipping.state === "US" ? "UPS" : "UPS Canada"}</span>
            </div>
            <div className="cs-summary-item">
              <span className="cs-summary-icon"><PinIcon /></span>
              <span>Delivering to {shipping.city}, {shipping.state}</span>
            </div>
          </div>

          {/* Buttons */}
          <div className="checkout-success-btns" style={{ animation: "cs-fade-up 0.5s ease 0.9s both" }}>
            <button className="btn-primary cs-btn-primary" onClick={() => navigate("/account")}>
              Track My Order →
            </button>
            <button className="btn-outline cs-btn-outline" onClick={() => navigate("/products")}>
              Continue Shopping
            </button>
          </div>
        </div>
      </div></>
    );
  }

  var uploadedCount = Object.keys(artwork).filter(function (k) { return artwork[k].url; }).length;
  var anyUploading = Object.keys(artwork).some(function (k) { return artwork[k].file && !artwork[k].url && !artwork[k].error; });
  // All items must have a successfully uploaded PDF before proceeding
  var allUploaded = items.length > 0 && items.every(function (item) {
    return artwork[item.cartId] && artwork[item.cartId].url;
  });

  return (
    <div className="checkout-layout">
      {/* LEFT */}
      <div className="checkout-left">

        {/* Step indicator */}
        <div className="checkout-steps">
          {["Shipping", "Delivery", "Artwork", "Payment"].map(function (label, i) {
            return (
              <div key={label} style={{ display: "flex", alignItems: "center" }}>
                <div className={"checkout-step" + (step >= i + 1 ? " active" : "")}>
                  <span className="step-num">{i + 1}</span>
                  <span>{label}</span>
                </div>
                {i < 3 && <div className="step-line" />}
              </div>
            );
          })}
        </div>

        {/* STEP 1: Shipping Address */}
        {step === 1 && (
          <form onSubmit={handleShippingInfoNext} className="checkout-form">
            <h3>Delivery Details</h3>
            <div className="checkout-grid">
              <div className="co-field" style={{ gridColumn: "1 / -1" }}>
                <label>Full Name *</label>
                <input name="fullName" value={shipping.fullName} onChange={handleShipping} placeholder="John Smith" required />
              </div>
              <div className="co-field">
                <label>Email Address *</label>
                <input name="email" type="email" value={shipping.email} onChange={handleShipping} placeholder="john@company.com" required />
              </div>
              <div className="co-field">
                <label>Phone Number</label>
                <input name="phone" value={shipping.phone} onChange={handleShipping} placeholder="416-555-0100" />
              </div>
              <div className="co-field" style={{ gridColumn: "1 / -1" }}>
                <label>Street Address *</label>
                <input name="address" value={shipping.address} onChange={handleShipping} placeholder="123 Main Street" required />
              </div>
              <div className="co-field" style={{ gridColumn: "1 / -1" }}>
                <label>Address Line 2</label>
                <input name="address2" value={shipping.address2} onChange={handleShipping} placeholder="Suite, Unit, etc. (optional)" />
              </div>
              <div className="co-field">
                <label>City *</label>
                <input name="city" value={shipping.city} onChange={handleShipping} placeholder="Toronto" required />
              </div>
              <div className="co-field">
                <label>Country *</label>
                <select
                  name="country"
                  value={shipping.country}
                  onChange={e => setShipping(s => ({ ...s, country: e.target.value, state: "" }))}
                >
                  <option value="CA">Canada</option>
                  <option value="US">United States</option>
                </select>
              </div>
              <div className="co-field">
                <label>Province / State *</label>
                <select name="state" value={shipping.state} onChange={handleShipping} required>
                  <option value="">Select {shipping.country === "CA" ? "Province" : "State"}</option>
                  {(shipping.country === "CA" ? CA_PROVINCES : US_STATES).map(s => (
                    <option key={s.code} value={s.code}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="co-field">
                <label>Postal / ZIP Code *</label>
                <input name="postcode" value={shipping.postcode} onChange={handleShipping} placeholder={shipping.country === "CA" ? "M5V 1A1" : "10001"} required />
              </div>
            </div>
            {cardError && <div className="co-error">{cardError}</div>}
            {shippingError && <div className="co-error">{shippingError}</div>}
            <button type="submit" className="btn-primary co-btn" disabled={shippingLoading}>
              {shippingLoading ? "Fetching shipping options..." : "Continue to Delivery →"}
            </button>
          </form>
        )}

        {/* STEP 2: Shipping Method */}
        {step === 2 && (
          <div className="checkout-form">
            <div className="co-back-row">
              <button type="button" className="co-back-btn" onClick={() => setStep(1)}>← Edit Address</button>
            </div>
            <h3>Choose Delivery Method</h3>
            <div className="co-shipping-options">
              {shippingOptions.map(function (opt, i) {
                var isSelected = selectedShipping && selectedShipping.service === opt.service;
                return (
                  <label key={i} className={"co-shipping-option" + (isSelected ? " selected" : "")}>
                    <input
                      type="radio"
                      name="shippingMethod"
                      checked={isSelected}
                      onChange={() => setSelectedShipping(opt)}
                    />
                    <div className="co-shipping-option-info">
                      <div>
                        <strong>{opt.service}</strong>
                        <span style={{ marginLeft: 8, color: "var(--text-muted)", fontSize: 13 }}>
                          {opt.estimatedDays} business day{opt.estimatedDays !== 1 ? "s" : ""}
                        </span>
                      </div>
                      <strong>${opt.price.toFixed(2)}</strong>
                    </div>
                  </label>
                );
              })}
              {shippingOptions.length === 0 && (
                <p style={{ color: "var(--text-muted)" }}>
                  {shippingError || "No shipping options available for this address."}{" "}
                  You can still upload your artwork — our team will contact you directly about delivery and payment.
                </p>
              )}
            </div>
            <button
              type="button"
              className="btn-primary co-btn"
              onClick={() => setStep(3)}
            >
              Continue to Artwork →
            </button>
          </div>
        )}

        {/* STEP 3: Artwork Upload */}
        {step === 3 && (
          <div className="checkout-form">
            <div className="co-back-row">
              <button type="button" className="co-back-btn" onClick={() => setStep(2)}>← Edit Delivery</button>
            </div>
            <h3>Upload Your Artwork</h3>
            <p className="co-stripe-note" style={{ marginBottom: 18 }}>
              Upload a print-ready file for each item. Accepted: PDF (max {MAX_FILE_SIZE_MB}MB).
            </p>
            {artworkError && <div className="co-error">{artworkError}</div>}
            <div className="co-artwork-list">
              {items.map(function (item) {
                var art = artwork[item.cartId];
                return (
                  <div key={item.cartId} className="co-artwork-item">
                    <div className="co-artwork-item-head">
                      <div className="co-item-img">
                        {item.imageUrl
                          ? <img src={item.imageUrl} alt={item.productName} />
                          : <div className="co-item-img-placeholder"><PrinterIcon /></div>}
                      </div>
                      <div className="co-item-info">
                        <strong>{item.productName}</strong>
                      </div>
                    </div>
                    {!art && (
                      <label className="co-upload-dropzone" style={{ borderColor: "rgba(239,68,68,0.4)", background: "rgba(239,68,68,0.03)" }}>
                        <UploadIcon />
                        <span>Upload PDF artwork <span style={{ color: "#f87171", fontSize: 11 }}>(required)</span></span>
                        <span style={{ fontSize: 11, color: "rgba(0,0,0,0.4)", marginTop: 2 }}>PDF only · Max 50MB</span>
                        <input type="file" accept=".pdf" style={{ display: "none" }}
                          onChange={function (e) { handleFileSelect(item.cartId, e.target.files); }} />
                      </label>
                    )}
                    {art && (
                      <div className="co-upload-file-row">
                        <span className="co-upload-file-icon"><FileIcon /></span>
                        <div className="co-upload-file-info">
                          <strong>{art.name}</strong>
                          <span>{formatBytes(art.size)}</span>
                          {!art.url && !art.error && (
                            <div className="co-progress-bar">
                              <div className="co-progress-fill" style={{ width: art.progress + "%" }} />
                            </div>
                          )}
                          {art.error && <span className="co-upload-error">{art.error}</span>}
                          {art.url && <span className="co-upload-success">✓ Uploaded</span>}
                        </div>
                        <button type="button" className="co-upload-remove" onClick={() => removeArtwork(item.cartId)}>
                          <TrashIcon />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="co-artwork-status">
              {uploadedCount} of {items.length} PDF{items.length !== 1 ? "s" : ""} uploaded
              {allUploaded && <span style={{ color: "#4ade80", marginLeft: 8, fontWeight: 700 }}>✓ All artworks ready</span>}
            </p>
            {!allUploaded && !anyUploading && (
              <p style={{ fontSize: 12, color: "#f5c200", marginBottom: 8 }}>
                Please upload a PDF artwork file for each item before continuing.
              </p>
            )}
            {selectedShipping ? (
              <button type="button" className="btn-primary co-btn" onClick={() => setStep(4)} disabled={anyUploading || !allUploaded}>
                {anyUploading ? "Uploading..." : !allUploaded ? `${uploadedCount} of ${items.length} artwork${items.length !== 1 ? "s" : ""} uploaded` : "Continue to Payment →"}
              </button>
            ) : (
              <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 8 }}>
                No delivery method is available yet, so online payment isn't possible. Send your cart and artwork to our team and we'll contact you directly.
              </p>
            )}
            <button
              type="button"
              className={selectedShipping ? "btn-outline co-btn" : "btn-primary co-btn"}
              style={selectedShipping ? { marginTop: 10 } : undefined}
              onClick={handleSendToTeam}
              disabled={anyUploading || followUpStatus === "saving" || followUpStatus === "saved"}
            >
              {followUpStatus === "saving" ? "Sending..." : followUpStatus === "saved" ? "✓ Sent — we'll be in touch" : "Send my cart & artwork to the team"}
            </button>
            {followUpStatus === "saved" && (
              <p style={{ fontSize: 13, color: "#16a34a", marginTop: 8 }}>
                Thanks! Our team has your cart{uploadedCount ? " and artwork" : ""} and will follow up at {shipping.email}.
              </p>
            )}
            {followUpStatus === "error" && <div className="co-error">Could not send your cart. Please try again.</div>}
          </div>
        )}

        {/* STEP 4: Payment */}
        {step === 4 && (
          <form onSubmit={handlePayment} className="checkout-form">
            <div className="co-back-row">
              <button type="button" className="co-back-btn" onClick={() => { setStep(3); setCardError(""); }}>
                ← Edit Artwork
              </button>
            </div>
            <div className="co-shipping-summary">
              <strong>{shipping.fullName}</strong>
              <span>{shipping.address}, {shipping.city}, {shipping.state} {shipping.postcode}</span>
              <span>{shipping.email}</span>
              {selectedShipping && <span>{selectedShipping.service} — ${selectedShipping.price.toFixed(2)}</span>}
            </div>
            <h3>Payment Details</h3>
            <p className="co-stripe-note">
              <LockIcon /> Secured by Stripe — your card details are never stored on our servers.
            </p>
            <div className="co-card-wrap">
              <CardElement options={CARD_ELEMENT_OPTIONS} onChange={function (e) { if (e.error) setCardError(e.error.message); else setCardError(""); }} />
            </div>
            {cardError && <div className="co-error">{cardError}</div>}
            <button type="submit" className="btn-primary co-btn" disabled={!stripe || processing}>
              {processing ? "Processing..." : "Pay $" + (discountedSubtotal + (selectedShipping?.price || 0) + (discountedSubtotal + (selectedShipping?.price || 0)) * HST_RATE).toFixed(2) + " →"}
            </button>
            <div className="co-trust">
              <span><ShieldIcon /> SSL Encrypted</span>
              <span><LockIcon /> PCI Compliant</span>
              <span><TruckIcon /> Fast Delivery</span>
            </div>
          </form>
        )}
      </div>

      {/* RIGHT: Order Summary */}
      <div className="checkout-right">
        <div className="co-summary-box">
          <h3>Order Summary</h3>
          <div className="co-summary-items">
            {items.map(function (item) {
              return (
                <div key={item.cartId} className="co-summary-item">
                  <div className="co-item-img">
                    {item.imageUrl
                      ? <img src={item.imageUrl} alt={item.productName} />
                      : <div className="co-item-img-placeholder"><PrinterIcon /></div>}
                  </div>
                  <div className="co-item-info">
                    <strong>{item.productName}</strong>
                    {item.optionLabels && Object.entries(item.optionLabels).slice(0, 3).map(([k, v]) => (
                      <span key={k} style={{ fontSize: 12 }}>{k}: {v}</span>
                    ))}
                  </div>
                  <div className="co-item-price">
                    {item.displayPrice > 0 ? "$" + item.displayPrice.toFixed(2) : "TBC"}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="co-summary-divider" />

          {/* Coupon code */}
          <div className="co-coupon-box">
            {!appliedCoupon ? (
              <>
                <div className="co-coupon-row">
                  <input
                    type="text"
                    placeholder="Coupon code"
                    value={couponInput}
                    onChange={(e) => { setCouponInput(e.target.value); if (couponStatus) setCouponStatus(null); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCoupon(); } }}
                  />
                  <button type="button" onClick={applyCoupon} disabled={couponStatus === "checking" || !couponInput.trim()}>
                    {couponStatus === "checking" ? "Checking..." : "Apply"}
                  </button>
                </div>
                {couponStatus === "invalid" && <p className="co-coupon-msg error">Invalid or expired coupon code.</p>}
              </>
            ) : (
              <div className="co-coupon-applied">
                <span>🏷️ <strong>{appliedCoupon.code}</strong> applied — {appliedCoupon.discountPercent}% off</span>
                <button type="button" onClick={removeCoupon}>Remove</button>
              </div>
            )}
          </div>

          <div className="co-summary-divider" />
          <div className="co-summary-row">
            <span>Shipping</span>
            <span>
              {selectedShipping
                ? "$" + selectedShipping.price.toFixed(2)
                : shippingLoading
                  ? "Calculating..."
                  : step === 1
                    ? "Not calculated"
                    : shippingOptions.length === 0
                      ? "No rates available"
                      : "Choose a method"}
            </span>
          </div>
          {appliedCoupon && (
            <div className="co-summary-row co-discount-row">
              <span>Discount ({appliedCoupon.discountPercent}%)</span>
              <span>-${discountAmount.toFixed(2)}</span>
            </div>
          )}
          <div className="co-summary-row">
            <span>Subtotal</span>
            <span>${(discountedSubtotal + (selectedShipping?.price || 0)).toFixed(2)}</span>
          </div>
          <div className="co-summary-row">
            <span>HST (13%)</span>
            <span>${((discountedSubtotal + (selectedShipping?.price || 0)) * HST_RATE).toFixed(2)}</span>
          </div>
          <p className="co-vat-note">HST # 805336690 RT0001</p>
          <div className="co-summary-row co-summary-total">
            <span>Total</span>
            <span>${(discountedSubtotal + (selectedShipping?.price || 0) + (discountedSubtotal + (selectedShipping?.price || 0)) * HST_RATE).toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Export ────────────────────────────────────────────────────────────
export default function CheckoutPage() {
  const { items } = useCart();
  const navigate = useNavigate();
  const [succeeded, setSucceeded] = useState(false);

  useEffect(function () {
    if (items.length === 0 && !succeeded) navigate("/cart");
  }, [items.length, succeeded, navigate]);

  if (items.length === 0 && !succeeded) return null;

  return (
    <div className="checkout-page">
      <div className="container">
        <div className="checkout-header"><h1>Checkout</h1></div>
        <Elements stripe={stripePromise}>
          <CheckoutForm succeeded={succeeded} setSucceeded={setSucceeded} />
        </Elements>
      </div>
    </div>
  );
}
